import $ from 'jquery';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { every, pick } from 'underscore';
import { Panel, PanelType } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Button, Input, Glyphicon } from '@bingads-webui-react/primitive';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';

const panelStyles = {
  main: {
    top: 60,
  },
};

const overlayProps = {
  styles: {
    root: {
      top: 60,
    },
  },
};

const flexStyle = {
  overflow: 'hidden',
  display: 'flex',
  flex: 1,
  position: 'relative',
  flexDirection: 'column',
};

function style() {
  return {
    panel: {
      '& .ms-Panel-scrollableContent, & .ms-Panel-contentInner, & .ms-Panel-content': {
        ...flexStyle,
      },
      '& .ms-Panel-content': {
        padding: 0,
      },
      '& input': {
        width: '90%',
        marginBottom: 10,
      },
    },
    header: {
      marginTop: 32,
      marginLeft: 28,
    },
    content: {
      ...flexStyle,
      marginLeft: 28,
      overflow: 'auto',
    },
    addSubBrandButton: {
      border: 'none',
      color: '#1470A5',
      width: 'fit-content',
      paddingLeft: 0,
      paddingRight: 5,
      '&:hover, &:active, &:focus': {
        color: '#0c4261',
        background: 'none',
        border: 'none',
        'border-color': 'none',
        'text-decoration': 'underline',
      },
    },
    saveButton: {
      marginRight: 12,
    },
    subBrandInput: {
      display: 'inline-block',
    },
    removeSubBrandButton: {
      padding: 0,
      border: 'none',
      height: 32,
      width: 32,
      minWidth: 'unset',
    },
    bottomBar: {
      borderTop: '1px solid #EDEBE9',
      padding: '0 16px',
      height: 63,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    closeButton: {
      position: 'absolute',
      right: 12,
      top: 14,
      padding: 0,
      border: 'none',
      minWidth: 'unset',
      height: 32,
      width: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .iconba': {
        color: '#605E5C',
        margin: 0,
      },
    },
    addSubBrandErrorMessage: {
      color: 'red',
    },
  };
}

const componentsAttributes = {
  panelClass: 'add-brand-panel',
  brandNameClass: 'brand-name',
  brandUrlClass: 'brand-website-url',
  addSubBrandButtonClass: 'add-sub-brand',
  addSubBrandErrorMessageClass: 'sub-brand-error',
  subBrandClass: 'sub-brand',
  removeSubBrandButtonClass: 'remove-sub-brand',
  saveButtonClass: 'save-brand',
  cancelButonClass: 'close-add-brand-panel',
  closeIconClass: 'close-add-brand-panel-icon',
};

const maxSubBrands = 10;
const maxSubBrandValueLength = 256;

class AddBrandPanel extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = this.initState();
  }

  onSaveBrand = () => {
    this.sendAddBrandRequest();
    this.props.onClose();
    this.resetState();
  }

  onClosePanel = () => {
    this.props.onClose();
    this.resetState();
  }

  initState() {
    return {
      brandName: null,
      brandUrl: null,
      subBrands: [],
      showAddSubBrandError: false,
    };
  }

  updateBrandName = (e) => {
    const { value } = e.currentTarget;
    this.setState({
      brandName: value,
    });
  }

  updateBrandUrl = (e) => {
    const { value } = e.currentTarget;
    this.setState({
      brandUrl: value,
    });
  }

  addSubBrand = () => {
    if (!this.canAddSubBrand()) {
      this.setState({
        showAddSubBrandError: true,
      });
      return;
    }
    const { subBrands } = this.state;
    this.setState({
      subBrands: [...subBrands, ''],
      showAddSubBrandError: false,
    });
  }

  canAddSubBrand = () => {
    const { subBrands } = this.state;
    return every(subBrands, subBrand => this.validateInput(subBrand));
  }

  updateSubBrand = (e) => {
    const { value, tabIndex } = e.currentTarget;
    const { subBrands } = this.state;
    const truncateValue = value.slice(0, maxSubBrandValueLength);
    this.setState({
      subBrands: [...subBrands.slice(0, tabIndex), truncateValue, ...subBrands.slice(tabIndex + 1)],
    }, this.updateSubBrandErrorMessage);
  }

  updateSubBrandErrorMessage = () => {
    if (this.canAddSubBrand()) {
      this.setState({
        showAddSubBrandError: false,
      });
    } else {
      this.setState({
        showAddSubBrandError: true,
      });
    }
  }

  removeSubBrand = (e) => {
    const { tabIndex } = e.currentTarget;
    const { subBrands } = this.state;
    this.setState({
      subBrands: [...subBrands.slice(0, tabIndex), ...subBrands.slice(tabIndex + 1)],
    }, this.updateSubBrandErrorMessage);
  }

  canSaveBrand = () => {
    const { brandName, brandUrl, subBrands } = this.state;
    return this.validateInput(brandName) && this.validateInput(brandUrl) && every(subBrands, subBrand => this.validateInput(subBrand));
  }

  validateInput = (input) => {
    if (input && input !== '') {
      return true;
    }

    return false;
  }

  showAddBrandError() {
    const { i18n } = this.props;
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(i18n.getString(_TL_('Request to add brand failed.')));
  }

  sendAddBrandRequest = () => {
    const { brandGrid } = this.props;
    const unblock = withUiBlock(brandGrid.grid.gridView.$el);
    const saveResult = pick(this.state, 'brandName', 'brandUrl', 'subBrands');
    $.ajax({
      url: `${brandGrid.getBmcMtEndpoint()}/api/v1/manufacturers/${brandGrid.customerId}/brands?cid=${brandGrid.customerId}&aid=${brandGrid.accountId}&uid=${brandGrid.userId}`,
      type: 'POST',
      data: saveResult,
      xhrFields: {
        withCredentials: true,
      },
      success: () => {
        unblock();
        brandGrid.fetchGridData = true;
        brandGrid.grid.gridView.refresh(true);
      },
      error: () => {
        unblock();
        this.showAddBrandError();
      },
    });
  }

  resetState() {
    this.setState(this.initState());
  }

  render() {
    const {
      displayName,
      isOpen,
      classes,
    } = this.props;

    const showSubBrand = this.state.subBrands.length > 0;
    const showAddSubBrand = this.state.subBrands.length < maxSubBrands;
    const isSaveButtonDisabled = !this.canSaveBrand();
    const { i18n } = this.props;
    const componentsStrings = {
      brandName: i18n.getString(_TL_('Brand name')),
      brandUrl: i18n.getString(_TL_('Brand domain URL')),
      addSubBrandText: i18n.getString(_TL_('Add sub-brand')),
      addSubBrandErrorMessage: i18n.getString(_TL_('Sub-brand(s) cannot be empty.')),
      subBrandName: i18n.getString(_TL_('Sub-brand(s)')),
      removeSubBrandButtonAriaLabel: i18n.getString(_TL_('remove sub brand')),
      saveButtonText: i18n.getString(_TL_('Save')),
      cancelButtonText: i18n.getString(_TL_('Cancel')),
      closeIconAriaLabel: i18n.getString(_TL_('Close')),
    };

    const showSubBrandContent = showSubBrand ? (
      <React.Fragment>
        <h4
          aria-label={componentsStrings.subBrandName}
        >
          {componentsStrings.subBrandName}
        </h4>
        {this.state.subBrands.map((subBrand, idx) => (
          <div>
            <Input
              className={classNames(classes.subBrandInput, `${componentsAttributes.subBrandClass}-${idx}`)}
              value={subBrand}
              key={`${componentsAttributes.subBrandClass}-${idx}`} // eslint-disable-line
              tabIndex={idx}
              onChange={this.updateSubBrand}
              aria-label={`${componentsStrings.subBrandName} ${idx}`}
            />
            <Button
              className={classNames(classes.removeSubBrandButton, `${componentsAttributes.removeSubBrandButtonClass}-${idx}`)}
              btnStyle="default"
              key={`${componentsAttributes.removeSubBrandButtonClass}-${idx}`} // eslint-disable-line
              tabIndex={idx}
              onClick={this.removeSubBrand}
              aria-label={`${componentsStrings.removeSubBrandButtonAriaLabel} ${idx}`}
            >
              <Glyphicon glyph="ba-Cancel" />
            </Button>
          </div>
        ))}
      </React.Fragment>
    ) : null;

    const showAddSubBrandErrorMessage = this.state.showAddSubBrandError ? (
      <div
        className={classNames(classes.addSubBrandErrorMessage, componentsAttributes.addSubBrandErrorMessageClass)}
        aria-label={componentsStrings.addSubBrandErrorMessage}
      >
        {componentsStrings.addSubBrandErrorMessage}
      </div>
    ) : null;

    const showAddSubBrandButton = showAddSubBrand ? (
      <React.Fragment>
        {showAddSubBrandErrorMessage}
        <Button
          className={classNames(classes.addSubBrandButton, componentsAttributes.addSubBrandButtonClass)}
          btnStyle="default"
          onClick={this.addSubBrand}
          aria-label={componentsStrings.addSubBrandText}
        >
          <Glyphicon glyph="ba-Add" />
          {componentsStrings.addSubBrandText}
        </Button>
      </React.Fragment>
    ) : null;

    return (
      <Panel
        className={classNames(classes.panel, componentsAttributes.panelClass)}
        type={PanelType.medium}
        isOpen={isOpen}
        hasCloseButton={false}
        styles={panelStyles}
        overlayProps={overlayProps}
      >
        <h2
          className={classes.header}
          aria-label={displayName}
        >
          {displayName}
        </h2>
        <div className={classes.content}>
          <h4
            aria-label={componentsStrings.brandName}
          >
            {componentsStrings.brandName}
          </h4>
          <Input
            className={componentsAttributes.brandNameClass}
            aria-label={componentsStrings.brandName}
            onChange={this.updateBrandName}
          />
          <h4
            aria-label={componentsStrings.brandUrl}
          >
            {componentsStrings.brandUrl}
          </h4>
          <Input
            className={componentsAttributes.brandUrlClass}
            aria-label={componentsStrings.brandUrl}
            onChange={this.updateBrandUrl}
          />
          {showSubBrandContent}
          {showAddSubBrandButton}
        </div>
        <div className={classes.bottomBar}>
          <Button
            className={classNames(classes.saveButton, componentsAttributes.saveButtonClass)}
            btnStyle="primary"
            onClick={this.onSaveBrand}
            disabled={isSaveButtonDisabled}
            aria-label={componentsStrings.saveButtonText}
          >
            {componentsStrings.saveButtonText}
          </Button>
          <Button
            className={componentsAttributes.cancelButonClass}
            onClick={this.onClosePanel}
            aria-label={componentsStrings.cancelButtonText}
          >
            {componentsStrings.cancelButtonText}
          </Button>
        </div>
        <Button
          className={classNames(classes.closeButton, componentsAttributes.closeIconClass)}
          btnStyle="default"
          onClick={this.onClosePanel}
          aria-label={componentsStrings.closeIconAriaLabel}
        >
          <Glyphicon glyph="ba-Cancel" />
        </Button>
      </Panel>
    );
  }
}

AddBrandPanel.propTypes = {
  displayName: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.object).isRequired,
  brandGrid: PropTypes.objectOf(PropTypes.object).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

AddBrandPanel.defaultProps = {
};

export const StyledAddBrandPanel = withDefaultStyles(memo(AddBrandPanel), style);
